import React, { useState, useRef } from 'react';
import { X, Sparkles, Loader2, Send, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { apiClient } from '../services/api';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface Props {
  cardIds: number[];
  onClose: () => void;
}

type Step = 'setup' | 'question' | 'result';

interface Question {
  question: string;
  words_tested: string[];
}

interface Evaluation {
  grammar: { has_errors: boolean; errors: string | null };
  naturalness: string;
  naturalness_reason: string | null;
  native_speaker_alternative: string;
  feedback: string;
}

export const CardSelectionContextualPracticeModal: React.FC<Props> = ({ cardIds, onClose }) => {
  useBodyScrollLock();
  const [step, setStep] = useState<Step>('setup');
  const [prompt, setPrompt] = useState('');
  const [question, setQuestion] = useState<Question | null>(null);
  const [answer, setAnswer] = useState('');
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [evaluateError, setEvaluateError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const generateQuestion = async () => {
    setIsGenerating(true);
    setGenerateError(null);
    setQuestion(null);
    setAnswer('');
    setEvaluation(null);
    setStep('question');
    try {
      const data = await apiClient.generateContextualQuestionFromCards({
        card_ids: cardIds,
        language: 'English',
        ...(prompt.trim() ? { prompt: prompt.trim() } : {}),
      });
      setQuestion(data);
      setTimeout(() => textareaRef.current?.focus(), 50);
    } catch {
      setGenerateError("Couldn't generate a question. Try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleEvaluate = async () => {
    if (!question || !answer.trim()) return;
    setIsEvaluating(true);
    setEvaluateError(null);
    try {
      const data = await apiClient.evaluateContextualAnswer({
        question: question.question,
        words_tested: question.words_tested,
        user_answer: answer.trim(),
        language: 'English',
      });
      setEvaluation(data);
      setStep('result');
    } catch {
      setEvaluateError("Couldn't evaluate your answer. Try again.");
    } finally {
      setIsEvaluating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-6">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 flex-shrink-0 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-1.5">
              <Sparkles className="h-4 w-4 text-purple-500" />
              Q&amp;A Practice
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {cardIds.length} card{cardIds.length !== 1 ? 's' : ''} selected
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

          {/* Setup step */}
          {step === 'setup' && (
            <>
              <div className="bg-purple-50 border border-purple-100 rounded-xl px-5 py-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  An AI question will be generated using your {cardIds.length} selected word{cardIds.length !== 1 ? 's' : ''}. You can optionally add context to guide the question.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Context <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) generateQuestion();
                  }}
                  placeholder="e.g. Describe a challenging work situation, a trip abroad…"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/70 focus:border-purple-300 resize-none"
                />
              </div>

              <button
                type="button"
                onClick={generateQuestion}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-xl hover:from-purple-600 hover:to-indigo-600 transition-all shadow-sm"
              >
                <Sparkles className="h-4 w-4" />
                Generate question
              </button>
            </>
          )}

          {/* Generating state */}
          {step === 'question' && isGenerating && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-gray-400">
              <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
              <p className="text-sm">Generating question…</p>
            </div>
          )}

          {/* Generate error */}
          {step === 'question' && generateError && !isGenerating && (
            <div className="flex flex-col items-center gap-3 py-8">
              <p className="text-sm text-red-600">{generateError}</p>
              <button
                type="button"
                onClick={generateQuestion}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Try again
              </button>
            </div>
          )}

          {/* Question step */}
          {step === 'question' && !isGenerating && !generateError && question && (
            <>
              {/* Question */}
              <div className="bg-purple-50 border border-purple-100 rounded-xl px-5 py-4">
                <p className="text-sm font-medium text-gray-900 leading-relaxed">
                  {question.words_tested.length > 0
                    ? <>
                        {question.question.replace(/[.?!]+$/, '')}
                        {', using '}
                        {question.words_tested.map((w, i) => (
                          <span key={i} className="text-purple-600">
                            {i > 0 ? ', ' : ''}{w}
                          </span>
                        ))}
                        {'.'}
                      </>
                    : question.question
                  }
                </p>
              </div>

              {prompt.trim() && (
                <p className="text-xs text-gray-400 -mt-1 px-1">
                  Context: <span className="italic">{prompt.trim()}</span>
                </p>
              )}

              {/* Answer textarea */}
              <textarea
                ref={textareaRef}
                value={answer}
                onChange={e => setAnswer(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && answer.trim() && !isEvaluating) {
                    handleEvaluate();
                  }
                }}
                placeholder="Write a phrase or sentence…"
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/70 focus:border-purple-300 resize-none"
                disabled={isEvaluating}
              />

              {evaluateError && (
                <p className="text-sm text-red-600">{evaluateError}</p>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={generateQuestion}
                  disabled={isEvaluating}
                  className="flex-1 px-3 py-2.5 text-sm font-medium border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  New question
                </button>
                <button
                  type="button"
                  onClick={handleEvaluate}
                  disabled={!answer.trim() || isEvaluating}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-xl hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  {isEvaluating
                    ? <><Loader2 className="h-4 w-4 animate-spin" />Evaluating…</>
                    : <><Send className="h-4 w-4" />Submit</>}
                </button>
              </div>
            </>
          )}

          {/* Result step */}
          {step === 'result' && question && evaluation && (
            <>
              {/* Answer recap */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                <p className="text-xs text-gray-400 mb-1">Your answer</p>
                <p className="text-sm text-gray-700 italic">&quot;{answer}&quot;</p>
              </div>

              {/* Feedback card */}
              <div className="border border-gray-200 rounded-xl divide-y divide-gray-100">
                <div className="px-4 py-3">
                  <p className="text-sm text-gray-800 font-medium">{evaluation.feedback}</p>
                </div>

                <div className="flex items-start gap-3 px-4 py-3">
                  {evaluation.grammar.has_errors
                    ? <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    : <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />}
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Grammar</p>
                    <p className="text-sm text-gray-700 mt-0.5">
                      {evaluation.grammar.has_errors ? evaluation.grammar.errors : 'No errors'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 px-4 py-3">
                  {evaluation.naturalness === 'correct'
                    ? <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    : <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />}
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Naturalness</p>
                    <p className="text-sm text-gray-700 mt-0.5 capitalize">
                      {evaluation.naturalness}
                      {evaluation.naturalness_reason && ` — ${evaluation.naturalness_reason}`}
                    </p>
                  </div>
                </div>

                <div className="px-4 py-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">A native speaker might say</p>
                  <p className="text-sm text-indigo-700 font-medium">&quot;{evaluation.native_speaker_alternative}&quot;</p>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={generateQuestion}
                  className="flex-1 px-3 py-2.5 text-sm font-medium border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Try again
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-3 py-2.5 text-sm font-medium bg-gray-900 text-white rounded-xl hover:bg-gray-700 transition-colors"
                >
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
